import { useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { SWAPPER_ABI } from '../contracts/abis';
import { CONTRACTS } from '../config/constants';
import { parseTokenAmount } from '../utils/helpers';
import web3Provider from '../utils/web3Provider';

// Test network connectivity
const testNetworkConnection = async () => {
  try {
    console.log('🌐 Testing network connection...');
    const provider = new ethers.JsonRpcProvider('https://rpc.fantom.network');
    const blockNumber = await provider.getBlockNumber();
    console.log('✅ Network test successful, current block:', blockNumber);
    return true;
  } catch (error) {
    console.error('❌ Network test failed:', error);
    return false;
  }
};

export const useSwapQuote = () => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchId, setLastFetchId] = useState(0);

  const fetchQuote = useCallback(async (fromToken, toToken, amount) => {
    console.log('🔍 fetchQuote called:', { fromToken: fromToken?.symbol, toToken: toToken?.symbol, amount });
    
    if (!fromToken || !toToken || !amount || parseFloat(amount) <= 0) {
      console.log('❌ Invalid input parameters');
      setQuote(null);
      setError(null);
      setLoading(false);
      return;
    }

    if (fromToken.address === toToken.address) {
      console.log('❌ Same token selected');
      setQuote(null);
      setError('Cannot swap same token');
      setLoading(false);
      return;
    }

    // Create unique fetch ID to prevent race conditions
    const fetchId = Date.now();
    console.log('🆔 Setting fetchId:', fetchId);
    setLastFetchId(fetchId);
    setLoading(true);
    setError(null);
    
    console.log('🚀 Starting quote fetch with ID:', fetchId);

    try {
      // Test network connection first
      const networkOk = await testNetworkConnection();
      if (!networkOk) {
        throw new Error('Network connection failed - please check your internet connection');
      }
      console.log('🔗 Checking provider...');
      let provider = web3Provider.getProvider();
      
      // If no wallet connected, use public provider for quotes
      if (!provider) {
        console.log('⚠️ No wallet provider, using public RPC...');
        try {
          provider = new ethers.JsonRpcProvider('https://rpc.fantom.network');
          console.log('✅ Public provider created');
        } catch (rpcError) {
          throw new Error('Cannot connect to Fantom network - check your internet connection');
        }
      } else {
        console.log('✅ Wallet provider available');
      }

      console.log('📋 Checking contract address:', CONTRACTS.SWAPPER);
      if (!CONTRACTS.SWAPPER) {
        throw new Error('Swapper contract not deployed');
      }
      console.log('✅ Contract address found');

      console.log('📄 Creating contract instance...');
      const swapperContract = new ethers.Contract(
        CONTRACTS.SWAPPER,
        SWAPPER_ABI,
        provider
      );
      console.log('✅ Contract instance created');

      // Parse input amount to wei
      console.log('💰 Parsing amount:', amount, 'for', fromToken.symbol);
      const amountInWei = parseTokenAmount(amount, fromToken.decimals);
      console.log('✅ Amount in wei:', amountInWei.toString());
      
      // Get token addresses (use zero address for native FTM)
      const fromTokenAddress = fromToken.isNative ? ethers.ZeroAddress : fromToken.address;
      const toTokenAddress = toToken.isNative ? ethers.ZeroAddress : toToken.address;
      console.log('🔗 Token addresses:', { from: fromTokenAddress, to: toTokenAddress });

      // Get quote from contract with timeout
      console.log('📞 Calling contract getQuote...');
      const quotePromise = swapperContract.getQuote(
        fromTokenAddress,
        toTokenAddress,
        amountInWei
      );
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Quote request timeout - check network connection')), 15000)
      );
      
      const [amountOut, feeAmount] = await Promise.race([quotePromise, timeoutPromise]);
      console.log('✅ Quote received:', { amountOut: amountOut.toString(), feeAmount: feeAmount.toString() });

      // Only update state if this is still the latest request
      setLastFetchId(currentFetchId => {
        if (fetchId >= currentFetchId) {
          const quoteData = {
            amountIn: amountInWei,
            amountOut: amountOut.toString(),
            feeAmount: feeAmount.toString(),
            fromToken,
            toToken,
            timestamp: Date.now()
          };

          setQuote(quoteData);
          setLoading(false);
          return fetchId;
        }
        return currentFetchId;
      });
    } catch (err) {
      // Only update error state if this is still the latest request  
      setLastFetchId(currentFetchId => {
        if (fetchId >= currentFetchId) {
          console.error('Error fetching swap quote:', err);
          setError(err.message || 'Failed to get quote');
          setQuote(null);
          setLoading(false);
          return fetchId;
        }
        return currentFetchId;
      });
    }
  }, []); // Remove lastFetchId dependency to prevent infinite loop

  return {
    quote,
    loading,
    error,
    fetchQuote
  };
};